import React, { Dispatch, useEffect, useMemo, useState } from "react"
import { get, find, isEmpty, findKey } from "lodash"
import { useDispatch } from "react-redux"
import uuid from "uuid"
import ClassNames from "classnames"
import { Panel } from "components/organisms/panel"
import { BaseTable, SeparateTable } from "components/molecules/table"
import { tableColumns } from "config/table_columns"
import MiddleChart from "components/molecules/chart/middleChart"
import { NewsList } from "components/organisms/panel/newsList"
import { APIResponse } from "redux/apis/types"
import {
  countryCodeList,
  requestPriceListAPIs,
  usePriceListAction,
} from "components/actions/priceList"
import { changeSymbol, changeSpModalSymbol } from "redux/ducks/Chart"
import { ROUTE_LIST } from "config/const"
import { MainContainer } from "components/molecules/utils/mainContainer"
import { checkIsSp } from "utils/util"
import { BaseSelect } from "components/atoms/input/select"

type InterestTemplateType = {
  interestList: APIResponse.Res
  interestEachCountryList: APIResponse.Res
  setInterestEachCountryList: Function
  interestListAction: Dispatch<usePriceListAction>
  transitionData: object
  countryList: Array<{
    id: number
    name: string
    icon: string
  }>
  setRefreshFlag: Function
}

export const InterestTemplate: React.FC<InterestTemplateType> = props => {
  const [currentPosition, changePosition] = useState<number>(0)
  const [currentEachCountryPosition, changeEachCountryPosition] = useState<
    number
  >(0)
  const [checkTransitionData, setCheckTransitionData] = useState(false)
  const [screenSize, setScreenSize] = useState(window.outerWidth)
  const [newsEffect, setNewsEffect] = useState(false)

  const interestList = get(props.interestList, "ResultList", [])
  const isSp = checkIsSp()
  const dispatch = useDispatch()
  const canvasId = useMemo(() => {
    return uuid()
  }, [])

  useEffect(() => {
    if (isEmpty(interestList[0])) return
    if (props.transitionData && !checkTransitionData) {
      const activePosition = findKey(interestList, (row: object) => {
        const symbol = get(props.transitionData, "Symbol")
        return get(row, "Symbol") === symbol
      })
      changePosition(Number(activePosition))
      dispatch(changeSymbol(canvasId, props.transitionData))
      setCheckTransitionData(true)
    } else {
      dispatch(changeSymbol(canvasId, interestList[0]))
    }
  }, [interestList, props.transitionData])

  useEffect(() => {
    if (isEmpty(interestList[0]) || isEmpty(props.interestEachCountryList))
      return
    const currentList = get(props.interestList, "ResultList")[currentPosition]
    const currentEachCountryList = get(
      props.interestEachCountryList,
      "ResultList"
    )
    const currentEachCountryKey = findKey(currentEachCountryList, {
      Symbol: get(currentList, "Symbol"),
    })
    changeEachCountryPosition(Number(currentEachCountryKey))
  }, [props.interestEachCountryList])

  useEffect(() => {
    window.addEventListener("resize", () => {
      setScreenSize(window.innerWidth)
    })
  })

  return (
    <MainContainer className="symmetry-layout top-interest">
      <div className="contents-block interest-list-block">
        <Panel
          title=""
          className={ClassNames("panel-interest-rate", { sp: isSp })}
          nonePanelHeader={!isSp}
          // refresh={() => {
          //   props.setRefreshFlag(true)
          //   setNewsEffect(true)
          // }}
          filter={
            isSp && (
              <BaseSelect
                option={props.countryList}
                value={currentPosition}
                onChange={(num: number) => {
                  changePosition(num)
                  const selectCountry = get(
                    props.interestList,
                    `ResultList[${num}]`,
                    []
                  )
                  const securityType = get(selectCountry, "SecurityType")
                  const countryData = find(countryCodeList, data => {
                    return data.securityType === securityType
                  })
                  if (countryData) {
                    requestPriceListAPIs({
                      name: get(countryData, "countryCode"),
                    })
                      .then((data: APIResponse.Res) => {
                        props.setInterestEachCountryList(data)
                      })
                      .catch(() => {
                        return
                      })
                  }
                }}
              />
            )
          }
        >
          <div className="result-list-block">
            {isSp ? (
              <BaseTable
                className="table-list-view table-liquid-cell"
                list={tableColumns.index.interest}
                active={currentEachCountryPosition}
                data={get(props.interestEachCountryList, "ResultList", [])}
                onClick={(data: any, i: number) => {
                  dispatch(changeSpModalSymbol(data))
                }}
              />
            ) : (
              <SeparateTable
                className="table-list-view table-liquid-cell"
                list={tableColumns.index.interest}
                data={get(props.interestList, "ResultList", [])}
                active={currentPosition}
                scrollable={screenSize > 559}
                onClick={(selectCountry: APIResponse.Res, i: number) => {
                  dispatch(changeSymbol(canvasId, selectCountry))
                  changePosition(i)
                  const securityType = get(selectCountry, "SecurityType")
                  const countryData = find(countryCodeList, data => {
                    return data.securityType === securityType
                  })
                  if (countryData) {
                    requestPriceListAPIs({
                      name: get(countryData, "countryCode"),
                    })
                      .then((data: APIResponse.Res) => {
                        props.setInterestEachCountryList(data)
                      })
                      .catch(() => {
                        return
                      })
                  }
                }}
              />
            )}
          </div>
        </Panel>
      </div>
      <div className="contents-block">
        <div className="contents-nest-block">
          {!isSp && (
            <Panel
              className="panel-list panel-interest-rate"
              title=""
              nonePanelHeader={true}
            >
              <MiddleChart id={canvasId} />
              <div className="interest-detail-list">
                <SeparateTable
                  className="table-list-view table-liquid-cell"
                  list={tableColumns.index.interest}
                  active={currentEachCountryPosition}
                  data={get(props.interestEachCountryList, "ResultList", [])}
                  onClick={(data: any, i: number) => {
                    dispatch(changeSymbol(canvasId, data))
                    changeEachCountryPosition(i)
                  }}
                  scrollable={
                    get(props.interestEachCountryList, "ResultList", [])
                      .length > 4 && screenSize > 559
                  }
                />
              </div>
            </Panel>
          )}
          <NewsList
            route={ROUTE_LIST.TOP_BONDS}
            effect={newsEffect}
            setEffect={setNewsEffect}
          />
        </div>
      </div>
    </MainContainer>
  )
}
